// Admin control panel: the kill switch, subsystem switches and today's spend
// against the caps. Everything that costs money starts OFF; turn things on in
// order (telemetry → agents → browser → feedback) and watch the meters.
import { useState } from 'react';
import { api } from '../../convex/_generated/api';
import { dayKey } from '../../convex/shared/time';
import type { ControlPanel as ControlPanelData } from '../../convex/shared/views';
import { fmtInt, fmtUsdPrecise } from '../lib/format';
import { useNow } from '../lib/hooks';
import { useSimMutation, useSimQuery } from '../lib/session';
import { Icon } from '../ui/Icon';
import { Button, Chip, Empty, Meter, Panel, Spinner, Toggle } from '../ui/primitives';
import { useToasts } from '../ui/Toasts';

type Switch = 'simPaused' | 'telemetryEnabled' | 'agentsEnabled' | 'browserEnabled' | 'feedbackEnabled';

const SWITCHES: { key: Switch; label: string; hint: string }[] = [
  { key: 'telemetryEnabled', label: 'Telemetry to aa-app dev', hint: 'VisionMax, Teltonika and DigitalMatter payloads to the dev edge. Dry run or live is set by the deployment env TELEMETRY_MODE.' },
  { key: 'agentsEnabled', label: 'Agents (System-1 + MiMo)', hint: 'Personas wake, decide and act. Costs Decider + gateway tokens.' },
  { key: 'browserEnabled', label: 'Sandbox browsers', hint: 'Personas operate aa-app in Browserbase. Costs browser minutes.' },
  { key: 'feedbackEnabled', label: 'Feedback to PostHog', hint: 'Reports that pass the System-1 check become PostHog support tickets.' },
  { key: 'simPaused', label: 'Pause the world', hint: 'Freezes trip departures and every loop. Vehicles finish nothing until resumed.' },
];

export function ControlPanel({ compact }: { compact?: boolean }) {
  const now = useNow(30_000);
  const panel = useSimQuery(api.agents.control.getControlPanel, { day: dayKey(now) });
  const setControl = useSimMutation(api.agents.control.setControl);
  const toasts = useToasts();
  const [busy, setBusy] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const apply = async (patch: Partial<Record<Switch | 'killSwitch', boolean>>, label: string) => {
    setBusy(label);
    try {
      await setControl({ ...patch, reason: reason.trim() || undefined });
      toasts.push(`${label} updated.`, { tone: 'leaf', icon: 'bolt' });
    } catch (err) {
      toasts.push(err instanceof Error ? err.message : 'Could not update.', { tone: 'danger', icon: 'alert' });
    } finally {
      setBusy(null);
    }
  };

  if (panel === undefined) return <Panel title="Control" icon="power"><Spinner /></Panel>;
  if (panel === null) {
    return (
      <Panel title="Control" icon="power">
        <Empty icon="power" title="No control row yet">Run the bootstrap (agents/bootstrap:seedAll) to create the switches.</Empty>
      </Panel>
    );
  }

  const killed = panel.killSwitch;
  return (
    <Panel title="Control" icon="power" tone={killed ? 'danger' : undefined} className="control-panel">
      <div className={`kill ${killed ? 'is-on' : ''}`}>
        <div>
          <strong>{killed ? 'Everything is stopped' : 'Simulation is live'}</strong>
          <p>
            {killed
              ? panel.killSwitchEnv
                ? 'PLAY_KILL_SWITCH=1 is set on the deployment; clear it there to resume.'
                : 'Wakes are cancelled and browsers released. Resume when ready.'
              : 'The kill switch stops agents, browsers, telemetry and feedback at once.'}
          </p>
          {panel.reason ? <p className="muted">Note: {panel.reason}</p> : null}
        </div>
        {killed ? (
          <Button
            tone="leaf"
            icon="play"
            size="lg"
            disabled={panel.killSwitchEnv || busy !== null}
            onClick={() => {
              if (window.confirm('Lift the kill switch? Subsystems that are switched on will start spending again.')) void apply({ killSwitch: false }, 'Kill switch');
            }}
          >
            Resume
          </Button>
        ) : (
          <Button tone="danger" icon="power" size="lg" disabled={busy !== null} onClick={() => void apply({ killSwitch: true }, 'Kill switch')}>
            Stop everything
          </Button>
        )}
      </div>

      <label className="field compact">
        <span>Note for the log (optional)</span>
        <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={200} placeholder="Why you are changing things" />
      </label>

      <div className="switches">
        {SWITCHES.map((s) => (
          <Toggle
            key={s.key}
            label={s.label}
            hint={compact ? undefined : s.hint}
            checked={panel[s.key]}
            disabled={busy !== null || (killed && s.key !== 'simPaused')}
            tone={s.key === 'simPaused' ? 'sun' : 'leaf'}
            onChange={(next) => void apply({ [s.key]: next }, s.label)}
          />
        ))}
      </div>

      <Spend panel={panel} />
    </Panel>
  );
}

function Spend({ panel }: { panel: ControlPanelData }) {
  const { spend, caps } = panel;
  return (
    <div className="spend">
      <h3>
        <Icon name="dollar" size={16} /> Spend today <small>{panel.day}</small>
      </h3>
      <Meter label="MiMo (gateway)" detail={`${fmtUsdPrecise(spend.llmUsd)} of ${fmtUsdPrecise(caps.llmUsd)}`} value={spend.llmUsd} max={caps.llmUsd} tone="coral" />
      <Meter label="Decider (System-1)" detail={`${fmtUsdPrecise(spend.deciderUsd)} of ${fmtUsdPrecise(caps.deciderUsd)}`} value={spend.deciderUsd} max={caps.deciderUsd} tone="grape" />
      <Meter label="Browser minutes" detail={`${spend.browserMinutes.toFixed(1)} of ${caps.browserMinutes}`} value={spend.browserMinutes} max={caps.browserMinutes} tone="sky" />
      <Meter label="Feedback reports" detail={`${spend.feedbackSent} of ${caps.feedback}`} value={spend.feedbackSent} max={caps.feedback} tone="leaf" />
      <div className="spend-counts">
        <Chip icon="brain">{fmtInt(spend.llmCalls)} MiMo calls</Chip>
        <Chip icon="sparkle">{fmtInt(spend.deciderCalls)} Decider calls</Chip>
        <Chip icon="send">{fmtInt(spend.edgePosts)} edge posts</Chip>
        <Chip icon="desktop">≤ {caps.concurrentBrowsers} browsers at once</Chip>
      </div>
    </div>
  );
}
